import React, {Suspense, useLayoutEffect, useRef, useState} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {useNavigate} from 'react-router';
import axios from 'axios';
import {diApiNa} from '@di-shared/api/di';
import type {ApiResponseTripTagListResponse, ApiResponseTripTagsResponse} from '@di-shared/api/di/__generated_na';
import {ERROR, UNIT} from '@di-shared/const';
import Header from '@di-shared/features/common/Header';
import HeaderBackButton from '@di-shared/features/common/HeaderBackButton';
import Popper from '@di-shared/features/common/Popper';
import useApiError from '@di-shared/features/common/useApiError';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import environmentStore from '@di-shared/features/environment/environmentStore';
import {HistoryTripDetailProps} from '@di-shared/features/history/trip-detail/HistoryTripDetail';
import {ROUTE_PATHS} from '@di-shared/routes';
import {roundToFixed} from '@di-shared/utils';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import useUnitConverter, {DistanceUnitSymbol} from '@di-shared/utils/useUnitConverter';
import {DistanceUnitType} from '@shared/cci/features/const/environment';
import {parseDateWithFormat} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import usePendingHandler from '@shared/usePendingHandler';
import {useQueryClient, useSuspenseQueries, useSuspenseQuery} from '@tanstack/react-query';

export default function HistoryTripDetailNa({vin, tripId}: HistoryTripDetailProps) {
  return (
    <ErrorBoundary fallback={<FallbackError />}>
      <Suspense fallback={<FallbackLoading />}>
        <Inner
          vin={vin}
          tripId={tripId}
        />
      </Suspense>
    </ErrorBoundary>
  );
}

const MINUTES_PER_HOUR = 60;

const MAP_HEIGHT = 210;
const MAP_ZOOM = 10;
const MERCATOR_TILE_SIZE = 256;

type GeoPoint = {lat: number; lon: number};

function getCenter(points: GeoPoint[]): GeoPoint {
  return {
    lat: points.reduce((sum, p) => sum + p.lat, 0) / points.length,
    lon: points.reduce((sum, p) => sum + p.lon, 0) / points.length,
  };
}

/** Web Mercator 투영(HERE mc 프로젝션과 동일 규약, 실제 API 응답과 대조 검증함) */
function projectToWorldPixel({lat, lon}: GeoPoint, zoom: number) {
  const worldSize = MERCATOR_TILE_SIZE * 2 ** zoom;
  const sinLat = Math.min(Math.max(Math.sin((lat * Math.PI) / 180), -0.9999), 0.9999);
  return {
    x: ((lon + 180) / 360) * worldSize,
    y: (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * worldSize,
  };
}

/** center 기준으로 렌더링된 지도 이미지(width x height) 안에서 point가 위치할 픽셀 좌표 */
function projectToMapPixel(point: GeoPoint, center: GeoPoint, zoom: number, width: number, height: number) {
  const centerPixel = projectToWorldPixel(center, zoom);
  const pointPixel = projectToWorldPixel(point, zoom);
  return {
    left: width / 2 + (pointPixel.x - centerPixel.x),
    top: height / 2 + (pointPixel.y - centerPixel.y),
  };
}

function buildHereStaticMapUrl(center: GeoPoint, zoom: number, width: number, height: number) {
  const apiKey = import.meta.env.VITE_HERE_API_KEY;
  return `https://image.maps.hereapi.com/mia/v3/base/mc/center:${center.lat},${center.lon};zoom=${zoom}/${width}x${height}/png?apiKey=${apiKey}`;
}

async function reverseGeocode(point: GeoPoint): Promise<string> {
  const apiKey = import.meta.env.VITE_HERE_API_KEY;
  const {data} = await axios.get('https://revgeocode.search.hereapi.com/v1/revgeocode', {
    params: {at: `${point.lat},${point.lon}`, apiKey},
  });
  return data.items?.[0]?.address?.label ?? ERROR.DASH;
}

function HistoryTripLocationMap({points}: {points: [GeoPoint, GeoPoint]}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number>();
  const center = getCenter(points);

  useLayoutEffect(() => {
    setWidth(containerRef.current?.offsetWidth);
  }, []);

  return (
    <div
      className="trip_location"
      ref={containerRef}
      style={{position: 'relative', overflow: 'hidden'}}
    >
      {width && (
        <>
          <img
            src={buildHereStaticMapUrl(center, MAP_ZOOM, width, MAP_HEIGHT)}
            alt=""
            style={{display: 'block', width: '100%', height: '100%', objectFit: 'cover'}}
          />
          <span
            className="ic ic_map_point_start"
            style={{
              position: 'absolute',
              transform: 'translate(-50%, -50%)',
              ...projectToMapPixel(points[0], center, MAP_ZOOM, width, MAP_HEIGHT),
            }}
          />
          <span
            className="ic ic_map_point_end"
            style={{
              position: 'absolute',
              transform: 'translate(-50%, -100%)',
              ...projectToMapPixel(points[1], center, MAP_ZOOM, width, MAP_HEIGHT),
            }}
          />
        </>
      )}
    </div>
  );
}

function Inner({vin, tripId}: HistoryTripDetailProps) {
  const {t} = useInferableTranslation();
  const {format: formatCldrDate} = useCldrDatetimeFormatter();
  const {convertDistanceValue, convertDistanceSymbol, convertSpeedValue, convertSpeedSymbol, isDistanceLessThan} = useUnitConverter();
  const {distanceUnit: appDistanceUnitName} = environmentStore.getState();

  const {data: tripDetail} = useSuspenseQuery({
    queryKey: [getQueryKey(diApiNa.drivingHistory.retrieveTripDetail), vin, tripId],
    queryFn: () => diApiNa.drivingHistory.retrieveTripDetail({vin, tripId}),
    select: ({data}) => data,
  });

  const ignitionOnDate = parseDateWithFormat(tripDetail.ignOnTime);
  const ignitionOffDate = parseDateWithFormat(tripDetail.ignOffTime);

  const drivingHours = Math.floor(tripDetail.drvTime / MINUTES_PER_HOUR);
  const drivingMinutes = tripDetail.drvTime % MINUTES_PER_HOUR;

  const headerDate = ignitionOnDate ? formatCldrDate(ignitionOnDate, 'MMMMEEEEd') : ERROR.DASH;
  const startTime = ignitionOnDate ? formatCldrDate(ignitionOnDate, 'aHM') : ERROR.DASH;
  const endTime = ignitionOffDate ? formatCldrDate(ignitionOffDate, 'aHM') : ERROR.DASH;

  const tripLocationPoints: [GeoPoint, GeoPoint] = [
    {lat: tripDetail.startLocation.latitude, lon: tripDetail.startLocation.longitude},
    {lat: tripDetail.endLocation.latitude, lon: tripDetail.endLocation.longitude},
  ];

  const [{data: startAddress}, {data: endAddress}] = useSuspenseQueries({
    queries: tripLocationPoints.map((point) => ({
      queryKey: [getQueryKey(reverseGeocode), point.lat, point.lon],
      queryFn: () => reverseGeocode(point),
    })),
  });

  return (
    <div className="wrap">
      <Header left={<HeaderBackButton />}>
        <h1 className="headline_xxsmall nav_center_title">{headerDate}</h1>
      </Header>
      <main
        id="content"
        className="content page_my_trips"
      >
        <section className="section section_trip_detail">
          <div className="driving_summary">
            <dl className="driving_summary_list">
              <div className="driving_summary_item">
                <dt className="driving_summary_title">
                  {t('ctn_mytrips_details_start_end_time_eu')}
                  <HistoryTripDetailTooltip />
                </dt>
                <dd className="driving_summary_info">
                  <span className="driving_summary_value">
                    {startTime} - {endTime}
                  </span>
                </dd>
              </div>
              <div className="driving_summary_item">
                <dt className="driving_summary_title">{t('ctn_mytrips_details_driving_time_eu')}</dt>
                <dd className="driving_summary_info">
                  {drivingHours > 0 && (
                    <>
                      <span className="driving_summary_value">{drivingHours}</span>
                      <span className="driving_summary_unit">{t('ctn_mytrips_details_time_h_01')}</span>
                    </>
                  )}
                  <span className="driving_summary_value">{drivingMinutes}</span>
                  <span className="driving_summary_unit">{t('ctn_mytrips_details_time_min_01')}</span>
                </dd>
              </div>
              <div className="driving_summary_item">
                <dt className="driving_summary_title">{t('ctn_mytrips_details_trip_distance_eu')}</dt>
                <dd className="driving_summary_info">
                  {isDistanceLessThan(tripDetail.drvDist) ? (
                    <span className="driving_summary_value">
                      {t('imsi_less_distance_$unit', {
                        unit: appDistanceUnitName === DistanceUnitType.KM ? DistanceUnitSymbol.KM : DistanceUnitSymbol.MILE,
                      })}
                    </span>
                  ) : (
                    <>
                      <span className="driving_summary_value">{convertDistanceValue(tripDetail.drvDist)}</span>
                      <span className="driving_summary_unit">{convertDistanceSymbol(tripDetail.drvDist)}</span>
                    </>
                  )}
                </dd>
              </div>
              <div className="driving_summary_item">
                <dt className="driving_summary_title">{t('ctn_mytrips_details_average_speed_eu')}</dt>
                <dd className="driving_summary_info">
                  <span className="driving_summary_value">{convertSpeedValue(tripDetail.avgSpeed)}</span>
                  <span className="driving_summary_unit">{convertSpeedSymbol(tripDetail.avgSpeed)}</span>
                </dd>
              </div>
              <div className="driving_summary_item">
                <dt className="driving_summary_title">{t('ctn_mytrips_details_top_speed')}</dt>
                <dd className="driving_summary_info">
                  <span className="driving_summary_value">{convertSpeedValue(tripDetail.maxSpeed)}</span>
                  <span className="driving_summary_unit">{convertSpeedSymbol(tripDetail.maxSpeed)}</span>
                </dd>
              </div>
              {!tripDetail.evEnergyUsage && (
                <div className="driving_summary_item">
                  <dt className="driving_summary_title">{t('ctn_mytrips_details_idle_time')}</dt>
                  <dd className="driving_summary_info">
                    <span className="driving_summary_value">{tripDetail.idleTime}</span>
                    <span className="driving_summary_unit">{t('ctn_mytrips_details_time_min_01')}</span>
                  </dd>
                </div>
              )}
              {tripDetail.evEnergyUsage && (
                <>
                  <div className="driving_summary_item">
                    <dt className="driving_summary_title">{t('ctn_mytrips_ev_energyuse')}</dt>
                    <dd className="driving_summary_info">
                      <span className="driving_summary_value">
                        {roundToFixed(tripDetail.evEnergyUsage.totalPowerConsumption / 1000, 1)}
                      </span>
                      <span className="driving_summary_unit">{UNIT.KWH}</span>
                    </dd>
                  </div>
                  <div className="driving_summary_item">
                    <dt className="driving_summary_title">{t('ctn_mytrips_ev_regenerative_01')}</dt>
                    <dd className="driving_summary_info">
                      <span className="driving_summary_value">{roundToFixed(tripDetail.evEnergyUsage.regenPower / 1000, 1)}</span>
                      <span className="driving_summary_unit">{UNIT.KWH}</span>
                    </dd>
                  </div>
                </>
              )}
            </dl>
          </div>
        </section>
        <section className="section section_trip_location">
          <div className="section_container_header">
            <h3 className="title_xxsmall">{t('ctn_mytrips_details_start_end_location')}</h3>
            <HistoryTripLocationTooltip />
          </div>
          <HistoryTripLocationMap points={tripLocationPoints} />
          <div className="trip_info_point">
            <ul className="trip_point_list">
              <li className="trip_point_item">
                <span className="trip_point">
                  <span className="ic ic_map_list_start" />
                </span>
                <div className="trip_point_container">
                  <span className="trip_point_text">{startAddress}</span>
                </div>
              </li>
              <li className="trip_point_item">
                <span className="trip_point">
                  <span className="ic ic_map_list_end" />
                </span>
                <div className="trip_point_container">
                  <span className="trip_point_text">{endAddress}</span>
                </div>
              </li>
            </ul>
          </div>
        </section>
        <section className="section section_tag">
          <HistoryTripTagsSection
            vin={vin}
            tripId={tripId}
          />
          {/**
           * [2026-08-05:Yevin Jang] FIXME: EU와 동일하게 보류
           * 트립 삭제가 화면상 미표출(DB flag 처리)인데 "delete"으로 표현하는 것에 대해 법인에서 우려 표출
           * GDPR 근거해 고객이 데이터 요청 시 법인에서는 CCS 서버에서 데이터 추출해 고객 제공
           * CCS 서버에서 추출할 경우 고객이 delete한 트립이 삭제되지 않고 살아있는 것을 확인하는 상황이 문제가 될 수 있음
           * 이 부분에 대해 법무검토가 필요함 (EU와 동일하게 NA도 배포 제외)
           */}
          {/*<div className="trip_delete_area">*/}
          {/*  <button*/}
          {/*    type="button"*/}
          {/*    className="btn_trip_delete"*/}
          {/*  >*/}
          {/*    {t('ctn_mytrips_details_delete_button_eu')}*/}
          {/*  </button>*/}
          {/*</div>*/}
        </section>
      </main>
    </div>
  );
}

function HistoryTripTagsSection({vin, tripId}: {vin: string; tripId: string}) {
  return (
    <ErrorBoundary fallback={<HistoryTripTagsSectionErrorFallback />}>
      <Suspense fallback={<HistoryTripTagsSectionLoading />}>
        <HistoryTripTagsSectionInner
          vin={vin}
          tripId={tripId}
        />
      </Suspense>
    </ErrorBoundary>
  );
}

function HistoryTripTagsSectionInner({vin, tripId}: {vin: string; tripId: string}) {
  const {t} = useInferableTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {resolveError} = useApiError();

  const [{data: availableTags}, {data: tripTags}] = useSuspenseQueries({
    queries: [
      {
        queryKey: [getQueryKey(diApiNa.tripTags.getAllTags)],
        queryFn: () => diApiNa.tripTags.getAllTags(),
        select: ({data}: ApiResponseTripTagListResponse) => data,
      },
      {
        queryKey: [getQueryKey(diApiNa.tripTags.getTagsForTrip), tripId],
        queryFn: () => diApiNa.tripTags.getTagsForTrip({tripId}),
        select: ({data}: ApiResponseTripTagsResponse) => data,
      },
    ],
  });

  const [addTag] = usePendingHandler(
    async (tagId: string) => {
      await diApiNa.tripTags.addTagToTrip({
        tripId,
        vin,
        tagId,
      });
      await queryClient.invalidateQueries({
        queryKey: [getQueryKey(diApiNa.tripTags.getTagsForTrip), tripId],
      });
      await queryClient.invalidateQueries({
        queryKey: [getQueryKey(diApiNa.drivingHistory.retrieveDailyTripHistory)],
      });
    },
    async (error) => {
      const [execute] = await resolveError(error);
      execute();
    }
  );

  const [removeTag] = usePendingHandler(
    async (tagId: string) => {
      await diApiNa.tripTags.removeTagFromTrip({
        tripId,
        vin,
        tagNameOrTagId: tagId,
      });
      await queryClient.invalidateQueries({
        queryKey: [getQueryKey(diApiNa.tripTags.getTagsForTrip), tripId],
      });
      await queryClient.invalidateQueries({
        queryKey: [getQueryKey(diApiNa.drivingHistory.retrieveDailyTripHistory)],
      });
    },
    async (error) => {
      const [execute] = await resolveError(error);
      execute();
    }
  );

  async function toggleTag(tagId: string) {
    const isTagApplied = tripTags.tags.some((tag) => tag.tagId === tagId);

    if (isTagApplied) {
      await removeTag(tagId);
    } else {
      await addTag(tagId);
    }
  }

  return (
    <>
      <HistoryTripTagsSectionHeader />
      <div className="tag_area">
        {availableTags.tags.map((tag) => {
          const isApplied = tripTags.tags.some((t) => t.tagId === tag.tagId);
          return (
            <button
              key={tag.tagId}
              type="button"
              className={isApplied ? 'btn btn_round is-selected' : 'btn btn_round'}
              onClick={() => toggleTag(tag.tagId)}
            >
              <span className="ic ic_check_16px" />
              {tag.tagName}
            </button>
          );
        })}
        <button
          type="button"
          className="btn btn_round btn_tag_add"
          onClick={() => {
            navigate(ROUTE_PATHS.HISTORY_TAGS.getFullPath(vin));
          }}
        >
          <span
            className="ic ic_add_14px"
            aria-hidden="true"
          />
          {t('ctn_mytrips_tags_create_button_eu')}
        </button>
      </div>
    </>
  );
}

function HistoryTripTagsSectionHeader() {
  const {t} = useInferableTranslation();

  return (
    <div className="section_container_header">
      <h3 className="title_xsmall_b">{t('ctn_mytrips_details_tags_title_eu')}</h3>
      <Popper
        placement="bottom"
        className="tooltip"
        marginLeft={1.5}
        tooltip={
          <div
            tabIndex={0}
            role="tooltip"
          >
            <p className="tooltip_text">{t('msg_mytrips_details_tags_tooltip_eu')}</p>
          </div>
        }
      >
        <div className="tooltip_area tooltip_area_single">
          <button
            type="button"
            className="btn_tooltip"
            aria-controls="tooltipStatement01"
            aria-expanded="false"
            aria-describedby="tooltipBox02"
          >
            <span
              className="ic ic_information_16px"
              aria-hidden="true"
            ></span>
            <span className="blind">{t('T10044')}</span>
          </button>
        </div>
      </Popper>
    </div>
  );
}

function HistoryTripTagsSectionLoading() {
  const {t} = useInferableTranslation();

  return (
    <>
      <HistoryTripTagsSectionHeader />
      <div className="tag_area">
        {Array.from({length: 3}).map((_, index) => (
          <span
            key={`tag-skeleton-${index}`}
            className="btn btn_round skeleton"
            style={{width: 72, height: 32}}
            aria-hidden="true"
          />
        ))}
      </div>
      <span className="blind">{t('ctn_mytrips_details_tags_title_eu')}</span>
    </>
  );
}

function HistoryTripTagsSectionErrorFallback() {
  const {t} = useInferableTranslation();

  return (
    <>
      <HistoryTripTagsSectionHeader />
      <div className="trip_info">
        <span className="blind">{t('ctn_mytrips_detail_resize')}</span>
        <div className="empty_area">
          <span className="ic ic_caution_32px"></span>
          <span
            className="body_small_r"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {t('msg_mytrips_tags_failed_to_load_eu') + '\n' + t('msg_mytrips_details_try_again_eu')}
          </span>
        </div>
      </div>
    </>
  );
}

function FallbackLoading() {
  const {t} = useInferableTranslation();
  return (
    <div className="wrap">
      <Header left={<HeaderBackButton />}>
        <h1 className="headline_xxsmall nav_center_title"></h1>
      </Header>
      <main
        id="content"
        className="content page_my_trips"
      >
        <section className="section section_trip_detail">
          <div className="driving_summary">
            <dl className="driving_summary_list">
              <div className="driving_summary_item">
                <dt className="driving_summary_title">
                  {t('ctn_mytrips_details_start_end_time_eu')}
                  <HistoryTripDetailTooltip />
                </dt>
                <dd
                  className="driving_summary_info skeleton"
                  style={{width: 100}}
                >
                  <span className="driving_summary_value">&nbsp;</span>
                </dd>
              </div>
              <div className="driving_summary_item">
                <dt className="driving_summary_title">{t('ctn_mytrips_details_driving_time_eu')}</dt>
                <dd
                  className="driving_summary_info skeleton"
                  style={{width: 100}}
                >
                  <span className="driving_summary_value">&nbsp;</span>
                </dd>
              </div>
              <div className="driving_summary_item">
                <dt className="driving_summary_title">{t('ctn_mytrips_details_trip_distance_eu')}</dt>
                <dd
                  className="driving_summary_info skeleton"
                  style={{width: 100}}
                >
                  <span className="driving_summary_value">&nbsp;</span>
                </dd>
              </div>
              <div className="driving_summary_item">
                <dt className="driving_summary_title">{t('ctn_mytrips_details_average_speed_eu')}</dt>
                <dd
                  className="driving_summary_info skeleton"
                  style={{width: 100}}
                >
                  <span className="driving_summary_value">&nbsp;</span>
                </dd>
              </div>
              <div className="driving_summary_item">
                <dt className="driving_summary_title">{t('ctn_mytrips_details_top_speed')}</dt>
                <dd
                  className="driving_summary_info skeleton"
                  style={{width: 100}}
                >
                  <span className="driving_summary_value">&nbsp;</span>
                </dd>
              </div>
            </dl>
          </div>
        </section>
        <section className="section section_trip_location">
          <div className="section_container_header">
            <h3 className="title_xxsmall">{t('ctn_mytrips_details_start_end_location')}</h3>
          </div>
          <div
            className="trip_location skeleton"
            aria-hidden="true"
          />
        </section>
        <section className="section section_tag">
          <HistoryTripTagsSectionLoading />
        </section>
      </main>
    </div>
  );
}

function FallbackError() {
  const {t} = useInferableTranslation();
  const {defaultDistanceSymbol, defaultSpeedSymbol} = useUnitConverter();
  return (
    <div className="wrap">
      <Header left={<HeaderBackButton />}>
        <h1 className="headline_xxsmall nav_center_title">{ERROR.DASH}</h1>
      </Header>
      <main
        id="content"
        className="content page_my_trips"
      >
        <section className="section section_trip_detail">
          <div className="driving_summary">
            <dl className="driving_summary_list">
              <div className="driving_summary_item">
                <dt className="driving_summary_title">
                  {t('ctn_mytrips_details_start_end_time_eu')}
                  <HistoryTripDetailTooltip />
                </dt>
                <dd className="driving_summary_info">
                  <span className="driving_summary_value">{ERROR.DASH}</span>
                </dd>
              </div>
              <div className="driving_summary_item">
                <dt className="driving_summary_title">{t('ctn_mytrips_details_driving_time_eu')}</dt>
                <dd className="driving_summary_info">
                  <span className="driving_summary_value">{ERROR.DASH}</span>
                  <span className="driving_summary_unit">{t('ctn_mytrips_details_time_h_01')}</span>
                  <span className="driving_summary_value">{ERROR.DASH}</span>
                  <span className="driving_summary_unit">{t('ctn_mytrips_details_time_min_01')}</span>
                </dd>
              </div>
              <div className="driving_summary_item">
                <dt className="driving_summary_title">{t('ctn_mytrips_details_trip_distance_eu')}</dt>
                <dd className="driving_summary_info">
                  <span className="driving_summary_value">{ERROR.DASH}</span>
                  <span className="driving_summary_unit">{defaultDistanceSymbol}</span>
                </dd>
              </div>
              <div className="driving_summary_item">
                <dt className="driving_summary_title">{t('ctn_mytrips_details_average_speed_eu')}</dt>
                <dd className="driving_summary_info">
                  <span className="driving_summary_value">{ERROR.DASH}</span>
                  <span className="driving_summary_unit">{defaultSpeedSymbol}</span>
                </dd>
              </div>
              <div className="driving_summary_item">
                <dt className="driving_summary_title">{t('ctn_mytrips_details_top_speed')}</dt>
                <dd className="driving_summary_info">
                  <span className="driving_summary_value">{ERROR.DASH}</span>
                  <span className="driving_summary_unit">{defaultSpeedSymbol}</span>
                </dd>
              </div>
            </dl>
          </div>
          <div className="trip_info">
            <div className="empty_area">
              <span className="ic ic_caution_32px"></span>
              <span
                className="body_small_r"
                style={{whiteSpace: 'pre-wrap'}}
              >
                {t('msg_mytrips_details_fail_load_data_eu') + '\n' + t('msg_mytrips_details_try_again_eu')}
              </span>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

function HistoryTripDetailTooltip() {
  const {t} = useInferableTranslation();
  return (
    <Popper
      placement="bottom"
      className="tooltip"
      marginLeft={3}
      tooltip={
        <div
          role="tooltip"
          tabIndex={0}
        >
          <p
            className="tooltip_text"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {t('msg_mytrips_monthly_distance_tooltip_eu')}
          </p>
        </div>
      }
    >
      <div className="tooltip_area tooltip_area_single">
        <button
          className="btn_tooltip"
          aria-expanded="false"
          type="button"
        >
          <span
            className="ic ic_information_16px"
            aria-hidden="true"
          />
        </button>
      </div>
    </Popper>
  );
}

/**
 * [2026-08-05:Yevin Jang] FIXME: 아래 "Location 섹션" 참고 (백엔드 위치 필드 부재로 mock 텍스트 하드코딩)
 * 다국어 채번 대기중 — 신규 문구라 t() 대신 하드코딩 텍스트 사용
 */
function HistoryTripLocationTooltip() {
  return (
    <Popper
      placement="bottom"
      className="tooltip"
      marginLeft={3}
      tooltip={
        <div
          role="tooltip"
          tabIndex={0}
        >
          <p
            className="tooltip_text"
            style={{whiteSpace: 'pre-wrap'}}
          >
            There may be inaccuracies depending on GPS conditions.
          </p>
        </div>
      }
    >
      <div className="tooltip_area tooltip_area_single">
        <button
          type="button"
          className="btn_tooltip"
          aria-expanded="false"
        >
          <span
            className="ic ic_information_16px"
            aria-hidden="true"
          />
        </button>
      </div>
    </Popper>
  );
}
